/**
 * IndexedDB persistence for the conversation.
 *
 * The database lives on the CLIENT's origin, next to whatever other scripts
 * that site runs, so nothing read back from it is trusted: `isValidRecord`
 * checks the shape before the component touches it (a malformed record used
 * to throw out of a `.then` and into the host page's console — review S14),
 * and the record is keyed by tenant so a snippet re-pointed at another tenant
 * never replays a stranger's transcript (review S3/N12).
 *
 * Everything here is best-effort and never throws: no IndexedDB (private
 * mode, old WebViews), a blocked open, a quota error — all read as "no saved
 * conversation".
 */
import type { StoredMessage } from './types';

const DB_NAME = 'pweb-chat';
const DB_VERSION = 1;
const STORE = 'conversations';

/** A saved conversation older than this is stale: start fresh instead of replaying it. */
export const RECORD_TTL_MS = 24 * 60 * 60 * 1000;

export interface ChatRecord {
  /** The tenant this transcript belongs to; a record for another tenant is discarded. */
  tenantId: string;
  messages: StoredMessage[];
  suggestions: string[];
  updatedAt: number;
  // Server-side conversation id (capability token) — sending it back keeps the
  // whole visit in ONE conversation instead of one per message.
  sessionId?: string;
  // Admin (human operator) message SKs already shown, so a full history fetch
  // after a page refresh cannot duplicate them.
  adminIds?: string[];
  // Poll cursor: newest message SK seen from GET /chat/history.
  lastSk?: string;
  /** The visitor already left their details in this conversation: no chip. */
  leadSubmitted?: boolean;
  /**
   * They already took an appointment in it: no booking chip either (1.18.0).
   * The two standing chips retire one at a time, so this is a flag of its own
   * rather than one "they have been in touch" between them.
   */
  bookingTaken?: boolean;
  /**
   * The conversation's language as the last reply said it (1.32.0, ADR-110
   * Am.11): after a reload the widget speaks it again before anyone writes.
   */
  language?: string;
}

export function recordKey(tenantId: string): string {
  return `current:${tenantId}`;
}

const ROLES = new Set(['user', 'assistant', 'admin', 'notice']);

function isStringArray(x: unknown): x is string[] {
  return Array.isArray(x) && x.every((s) => typeof s === 'string');
}

/** Shape check for a record read back from the client's origin. */
export function isValidRecord(x: unknown, tenantId: string): x is ChatRecord {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
  const r = x as Record<string, unknown>;
  if (r.tenantId !== tenantId) return false;
  if (typeof r.updatedAt !== 'number' || !Number.isFinite(r.updatedAt)) return false;
  if (!Array.isArray(r.messages)) return false;
  for (const m of r.messages) {
    if (!m || typeof m !== 'object') return false;
    const msg = m as Record<string, unknown>;
    if (typeof msg.role !== 'string' || !ROLES.has(msg.role)) return false;
    if (typeof msg.content !== 'string') return false;
    if (msg.error !== undefined && typeof msg.error !== 'boolean') return false;
    if (msg.language !== undefined && typeof msg.language !== 'string') return false;
  }
  if (!isStringArray(r.suggestions)) return false;
  if (r.sessionId !== undefined && typeof r.sessionId !== 'string') return false;
  if (r.adminIds !== undefined && !isStringArray(r.adminIds)) return false;
  if (r.lastSk !== undefined && typeof r.lastSk !== 'string') return false;
  if (r.leadSubmitted !== undefined && typeof r.leadSubmitted !== 'boolean') return false;
  if (r.bookingTaken !== undefined && typeof r.bookingTaken !== 'boolean') return false;
  if (r.language !== undefined && typeof r.language !== 'string') return false;
  return true;
}

export function isFresh(record: ChatRecord, now = Date.now()): boolean {
  return now - record.updatedAt < RECORD_TTL_MS;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

/** The saved conversation for this tenant, or null when there is none, it is malformed, or storage is unavailable. */
export async function loadChat(tenantId: string): Promise<ChatRecord | null> {
  try {
    const db = await openDb();
    return await new Promise<ChatRecord | null>((resolve) => {
      try {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(recordKey(tenantId));
        req.onsuccess = () => resolve(isValidRecord(req.result, tenantId) ? req.result : null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  } catch {
    return null;
  }
}

export async function saveChat(tenantId: string, record: ChatRecord): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record, recordKey(tenantId));
  } catch {
    // Persistence is best-effort.
  }
}

export async function clearChat(tenantId: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(recordKey(tenantId));
  } catch {
    // Persistence is best-effort.
  }
}
